import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { GameEvent } from '@src/core/EventQueue';
import { giveWeapon, playClip, spawnFx, spawnProjectile, type ProjectileSpec } from '@src/ecs/archetypes';
import { ClipId, Dying, Enemy, Health, Player, Position, Radius, Spawner, Sprite, SpriteId, Weapon, Anim } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, RoomPhase, type GameContext } from '@src/ecs/world';
import { dealDamage } from '@src/game/Combat';
import { FirePattern, WeaponId, weaponDef, type WeaponDef } from '@src/game/weapons/WeaponDefs';

const DEG = Math.PI / 180;
const CANDIDATES = 256;
const OVERCHARGE_PULSE_PERIOD = 0.6;

/**
 * Fires the held weapon for every player: bolts, crescents, phase bolts and the
 * lightning chain beam. Handles weapon expiry and Overcharge decay.
 */
export class WeaponSystem implements GameSystem {
  private ctx!: GameContext;
  private readonly spec: ProjectileSpec = {
    owner: 0,
    weapon: WeaponId.Wand,
    sprite: SpriteId.BoltWand,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    radius: 0,
    life: 0,
    damage: 0,
    spawnerMult: 1,
    knockback: 0,
    pierce: 0,
    tint: 0xffffff,
    scale: 1,
    grow: 0,
  };
  private readonly candidates = new Int32Array(CANDIDATES);
  private readonly visit = new Uint32Array(Config.sim.maxEntities);
  private stamp = 1;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    ctx.beams.count = 0;
    const players = query(ctx.world, [Player, Weapon, Position]);

    for (let i = 0; i < players.length; i++) {
      const eid = players[i];
      if (Player.dead[eid]) continue;
      const def = weaponDef(Weapon.typeId[eid]);

      if (def.duration > 0) {
        Weapon.timeLeft[eid] -= dt;
        if (Weapon.timeLeft[eid] <= 0) {
          giveWeapon(eid, WeaponId.Wand);
          ctx.events.push(GameEvent.WeaponExpired, eid, def.id, Position.x[eid], Position.y[eid]);
          continue;
        }
      }
      if (Weapon.overcharge[eid] > 0) Weapon.overcharge[eid] -= dt;
      if (Weapon.cooldown[eid] > 0) Weapon.cooldown[eid] -= dt;
      if (Player.recoil[eid] > 0) Player.recoil[eid] = Math.max(0, Player.recoil[eid] - dt * 7.5);
      if (ctx.room.phase === RoomPhase.Victory) {
        Player.firing[eid] = 0;
        continue;
      }

      const firing = Player.firing[eid] === 1;
      if (def.pattern === FirePattern.Beam) {
        if (firing) {
          this.fireBeam(eid, def, dt);
          Player.recoil[eid] = 0.45 + Math.sin(ctx.time * 40) * 0.2;
          // Continuous beam keeps a soft cast lean without locking the clip.
          if (Anim.clip[eid] !== ClipId.Cast) playClip(eid, ClipId.Cast);
        }
        continue;
      }
      if (def.pattern === FirePattern.Detonate) {
        if (!firing || Weapon.cooldown[eid] > 0) continue;
        this.fireCataclysm(eid, def);
        playClip(eid, ClipId.Cast, true, false);
        Sprite.squash[eid] = Math.max(Sprite.squash[eid], 0.55);
        ctx.events.push(GameEvent.Shot, eid, def.id, Position.x[eid], Position.y[eid]);
        ctx.events.push(GameEvent.Cataclysm, eid, def.id, Position.x[eid], Position.y[eid]);
        continue;
      }
      if (!firing || Weapon.cooldown[eid] > 0) continue;

      Weapon.cooldown[eid] += 1 / def.fireRate;
      switch (def.pattern) {
        case FirePattern.Bolt:
          this.fireBolt(eid, def);
          this.muzzle(eid, SpriteId.MuzzleWand, 1.05, def.tint);
          Player.recoil[eid] = 0.85;
          break;
        case FirePattern.Crescent:
          this.fireCrescent(eid, def);
          this.muzzle(eid, SpriteId.MuzzleFire, 1.55, def.tint);
          Player.recoil[eid] = 1.15;
          break;
        case FirePattern.PhaseBolt:
          this.firePhase(eid, def);
          this.muzzle(eid, SpriteId.MuzzlePhase, 1.2, def.tint);
          Player.recoil[eid] = 1;
          break;
        default:
          break;
      }
      // Punchy cast restart on every discrete shot so the thrust reads even at high fire rate.
      playClip(eid, ClipId.Cast, true, false);
      Sprite.squash[eid] = Math.max(Sprite.squash[eid], 0.35);
      ctx.events.push(GameEvent.Shot, eid, def.id, Position.x[eid], Position.y[eid]);
    }
  }

  /** Muzzle flash sprite at the wand tip plus a spray of sparks along the aim. */
  private muzzle(eid: number, sprite: SpriteId, scale: number, tint: number): void {
    const ax = Player.aimX[eid];
    const ay = Player.aimY[eid];
    const reach = Config.anim.player.muzzleReach;
    const x = Position.x[eid] + ax * reach;
    const y = Position.y[eid] + ay * reach;
    const oc = Weapon.overcharge[eid] > 0;
    spawnFx(this.ctx, sprite, x, y, Math.atan2(ay, ax), scale * (oc ? 1.7 : 1), oc ? 0xfff2a0 : 0xffffff, 0.86);
    emitParticles(this.ctx.fx, ParticleKind.MuzzleSparks, x, y, ax, ay, oc ? 1.35 : 0.65, tint);
  }

  private prepSpec(eid: number, def: WeaponDef, sprite: SpriteId): ProjectileSpec {
    const s = this.spec;
    const oc = Weapon.overcharge[eid] > 0;
    s.owner = eid;
    s.weapon = def.id;
    s.sprite = sprite;
    s.x = Position.x[eid];
    s.y = Position.y[eid];
    s.radius = def.projectileRadius * (oc ? 1.6 : 1);
    s.life = def.projectileLife;
    s.damage = def.damage * (oc ? 2 : 1);
    s.spawnerMult = def.spawnerMult * (oc ? 1.5 : 1);
    s.knockback = def.knockback * (oc ? 1.5 : 1);
    s.pierce = def.pierce;
    s.tint = def.tint;
    s.scale = s.radius * 2;
    s.grow = 0;
    return s;
  }

  /** One charge: smash every living enemy in the room, bruise spawners, then the wand is back. */
  private fireCataclysm(eid: number, def: WeaponDef): void {
    const ctx = this.ctx;
    const px = Position.x[eid];
    const py = Position.y[eid];
    spawnFx(ctx, SpriteId.BombBlast, px, py, ctx.rng.next() * Math.PI * 2, 6.2, 0xffffff, 0.88);
    spawnFx(ctx, SpriteId.Shockwave, px, py, 0, 9.5, def.tint, 0.7);
    spawnFx(ctx, SpriteId.Shockwave, 0, 0, 0, 12, 0xfff0a0, 0.62);
    emitParticles(ctx.fx, ParticleKind.Embers, px, py, 0, 0, 2.8, def.tint);
    emitParticles(ctx.fx, ParticleKind.PortalBurst, px, py, 0, 0, 2.2, def.tint);
    emitParticles(ctx.fx, ParticleKind.HitSpark, px, py, 0, 0, 2.0, 0xfff2a0);
    ctx.fx.hitStopMs = Math.max(ctx.fx.hitStopMs, 160);
    ctx.fx.flash = Math.max(ctx.fx.flash, 0.85);
    ctx.fx.rumble = 1;
    ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 2.2;
    ctx.fx.shakeY += (ctx.rng.next() - 0.5) * 2.2;
    Player.recoil[eid] = 1.4;

    let bursts = 0;
    const enemies = query(ctx.world, [Enemy, Position, Health]);
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i];
      if (Dying.flag[e] || Health.hp[e] <= 0) continue;
      const ex = Position.x[e];
      const ey = Position.y[e];
      const dx = ex - px;
      const dy = ey - py;
      const d = Math.hypot(dx, dy) || 1;
      dealDamage(ctx, e, def.damage, (dx / d) * def.knockback, (dy / d) * def.knockback, def.spawnerMult, def.id);
      if (bursts < 10) {
        spawnFx(ctx, SpriteId.BombBlast, ex, ey, ctx.rng.next() * Math.PI * 2, 2.4, 0xffffff, 0.75);
        bursts++;
      }
    }

    const spawners = query(ctx.world, [Spawner, Position, Health]);
    for (let i = 0; i < spawners.length; i++) {
      const s = spawners[i];
      if (Dying.flag[s] || Health.hp[s] <= 0) continue;
      const sx = Position.x[s];
      const sy = Position.y[s];
      const dx = sx - px;
      const dy = sy - py;
      const d = Math.hypot(dx, dy) || 1;
      dealDamage(ctx, s, def.damage, (dx / d) * def.knockback * 0.4, (dy / d) * def.knockback * 0.4, def.spawnerMult, def.id);
      spawnFx(ctx, SpriteId.Shockwave, sx, sy, 0, 2.2, def.tint, 0.6);
    }

    giveWeapon(eid, WeaponId.Wand);
  }

  private fireBolt(eid: number, def: WeaponDef): void {
    const s = this.prepSpec(eid, def, SpriteId.BoltWand);
    const oc = Weapon.overcharge[eid] > 0;
    const speed = def.projectileSpeed * (oc ? 1.3 : 1);
    const jitter = (this.ctx.rng.next() - 0.5) * 3 * DEG;
    const a = Math.atan2(Player.aimY[eid], Player.aimX[eid]) + jitter;
    s.vx = Math.cos(a) * speed;
    s.vy = Math.sin(a) * speed;
    s.x += Math.cos(a) * Config.anim.player.muzzleReach * 0.55;
    s.y += Math.sin(a) * Config.anim.player.muzzleReach * 0.55;
    s.scale = 0.36;
    spawnProjectile(this.ctx, s);
  }

  private fireCrescent(eid: number, def: WeaponDef): void {
    const oc = Weapon.overcharge[eid] > 0;
    const count = oc ? def.overchargeCount : def.count;
    const arc = (oc ? def.overchargeArcDeg : def.arcDeg) * DEG;
    const center = Math.atan2(Player.aimY[eid], Player.aimX[eid]);
    const speed = def.projectileSpeed * (oc ? 1.3 : 1);
    const full = arc >= Math.PI * 2 - 0.001;
    for (let i = 0; i < count; i++) {
      const s = this.prepSpec(eid, def, SpriteId.BoltFire);
      const t = count === 1 ? 0.5 : i / (full ? count : count - 1);
      const a = center - arc / 2 + arc * t;
      s.vx = Math.cos(a) * speed;
      s.vy = Math.sin(a) * speed;
      s.x += Math.cos(a) * Config.anim.player.muzzleReach * 0.65;
      s.y += Math.sin(a) * Config.anim.player.muzzleReach * 0.65;
      s.scale = s.radius * 2.6;
      spawnProjectile(this.ctx, s);
    }
    // One animated crescent carries the visual for the whole fan (two for a full ring).
    const px = Position.x[eid];
    const py = Position.y[eid];
    const slashScale = full ? 5.2 : 4.2;
    const tip = Config.anim.player.muzzleReach;
    spawnFx(this.ctx, SpriteId.Slash, px + Math.cos(center) * tip, py + Math.sin(center) * tip, center, slashScale, 0xffffff, 0.87);
    if (full) spawnFx(this.ctx, SpriteId.Slash, px - Math.cos(center) * tip, py - Math.sin(center) * tip, center + Math.PI, slashScale, 0xffffff, 0.87);
    emitParticles(this.ctx.fx, ParticleKind.Embers, px + Math.cos(center) * (tip + 0.55), py + Math.sin(center) * (tip + 0.55), Math.cos(center), Math.sin(center), oc ? 2 : 1);
    const recoil = oc ? 0 : 1.2;
    this.ctx.fx.shakeX -= Player.aimX[eid] * 0.12 * recoil;
    this.ctx.fx.shakeY -= Player.aimY[eid] * 0.12 * recoil;
  }

  private firePhase(eid: number, def: WeaponDef): void {
    const oc = Weapon.overcharge[eid] > 0;
    const count = oc ? def.overchargeCount : def.count;
    const spread = (oc ? def.overchargeArcDeg : def.arcDeg) * DEG;
    const center = Math.atan2(Player.aimY[eid], Player.aimX[eid]);
    const speed = def.projectileSpeed * (oc ? 1.3 : 1);
    for (let i = 0; i < count; i++) {
      const s = this.prepSpec(eid, def, SpriteId.BoltPhase);
      const t = count === 1 ? 0.5 : i / (count - 1);
      const a = center - spread / 2 + spread * t;
      s.vx = Math.cos(a) * speed;
      s.vy = Math.sin(a) * speed;
      s.x += Math.cos(a) * Config.anim.player.muzzleReach * 0.55;
      s.y += Math.sin(a) * Config.anim.player.muzzleReach * 0.55;
      s.scale = 0.9;
      spawnProjectile(this.ctx, s);
    }
  }

  private fireBeam(eid: number, def: WeaponDef, dt: number): void {
    const ctx = this.ctx;
    const oc = Weapon.overcharge[eid] > 0;
    const maxChain = oc ? def.overchargeCount : def.count;
    const dmg = def.damage * (oc ? 2 : 1) * dt;
    const chainRange = def.chainRange * (oc ? 1.4 : 1);
    const stamp = ++this.stamp;
    if (stamp === 0xffffffff) this.visit.fill(0);

    let cx = Position.x[eid];
    let cy = Position.y[eid];
    const ax = Player.aimX[eid];
    const ay = Player.aimY[eid];
    this.visit[eid] = stamp;

    // First hop: best target in the aim cone within beamRange.
    let target = this.findConeTarget(cx, cy, ax, ay, def.beamRange);
    if (target === -1) {
      this.pushBeam(cx, cy, cx + ax * def.beamRange, cy + ay * def.beamRange);
    }

    let hops = 0;
    while (target !== -1 && hops < maxChain) {
      this.visit[target] = stamp;
      const tx = Position.x[target];
      const ty = Position.y[target];
      this.pushBeam(cx, cy, tx, ty);
      const dx = tx - cx;
      const dy = ty - cy;
      const d = Math.hypot(dx, dy) || 1;
      dealDamage(ctx, target, dmg, (dx / d) * def.knockback, (dy / d) * def.knockback, def.spawnerMult, def.id);
      // Node sparks: a few per second per hop, not per tick.
      if (ctx.rng.chance(dt * 6)) emitParticles(ctx.fx, ParticleKind.HitSpark, tx, ty, -dx / d, -dy / d, 0.5, 0xbfffff);
      cx = tx;
      cy = ty;
      hops++;
      target = this.findChainTarget(cx, cy, chainRange, stamp);
    }

    if (!oc) return;
    Weapon.pulse[eid] += dt;
    if (Weapon.pulse[eid] < OVERCHARGE_PULSE_PERIOD) return;
    Weapon.pulse[eid] = 0;
    const s = this.prepSpec(eid, def, SpriteId.Shockwave);
    s.vx = 0;
    s.vy = 0;
    s.radius = 0.6;
    s.life = 0.45;
    s.damage = def.damage * 0.6;
    s.spawnerMult = 0.5;
    s.knockback = 8;
    s.pierce = 999;
    s.grow = 14;
    s.scale = 1.2;
    spawnProjectile(ctx, s);
    ctx.events.push(GameEvent.Shockwave, eid, def.id, Position.x[eid], Position.y[eid]);
    ctx.fx.shakeX += (ctx.rng.next() - 0.5) * 0.6;
    ctx.fx.shakeY += (ctx.rng.next() - 0.5) * 0.6;
  }

  private pushBeam(x1: number, y1: number, x2: number, y2: number): void {
    const b = this.ctx.beams;
    if (b.count >= b.capacity) return;
    const o = b.count * 4;
    b.data[o] = x1;
    b.data[o + 1] = y1;
    b.data[o + 2] = x2;
    b.data[o + 3] = y2;
    b.count++;
  }

  private findConeTarget(x: number, y: number, ax: number, ay: number, range: number): number {
    const ctx = this.ctx;
    let best = -1;
    let bestScore = Infinity;

    const n = ctx.grid.gather(x, y, range, this.candidates);
    for (let i = 0; i < n; i++) {
      const e = this.candidates[i];
      if (Dying.flag[e] || Health.hp[e] <= 0) continue;
      const dx = Position.x[e] - x;
      const dy = Position.y[e] - y;
      const d = Math.hypot(dx, dy);
      if (d > range || d < 0.01) continue;
      const cos = (dx * ax + dy * ay) / d;
      if (cos < 0.55) continue;
      const score = d * (2 - cos);
      if (score >= bestScore) continue;
      bestScore = score;
      best = e;
    }

    const spawners = query(ctx.world, [Spawner, Position, Radius, Health]);
    for (let i = 0; i < spawners.length; i++) {
      const s = spawners[i];
      if (Dying.flag[s] || Health.hp[s] <= 0) continue;
      const dx = Position.x[s] - x;
      const dy = Position.y[s] - y;
      const d = Math.hypot(dx, dy);
      if (d > range + Radius.r[s] || d < 0.01) continue;
      const cos = (dx * ax + dy * ay) / d;
      if (cos < 0.8) continue;
      const score = d * (2 - cos) * 0.9;
      if (score >= bestScore) continue;
      bestScore = score;
      best = s;
    }
    return best;
  }

  private findChainTarget(x: number, y: number, range: number, stamp: number): number {
    const ctx = this.ctx;
    let best = -1;
    let bestD = range * range;
    const n = ctx.grid.gather(x, y, range, this.candidates);
    for (let i = 0; i < n; i++) {
      const e = this.candidates[i];
      if (this.visit[e] === stamp || Dying.flag[e] || Health.hp[e] <= 0) continue;
      const dx = Position.x[e] - x;
      const dy = Position.y[e] - y;
      const d2 = dx * dx + dy * dy;
      if (d2 >= bestD) continue;
      bestD = d2;
      best = e;
    }
    if (best !== -1) return best;

    const spawners = query(ctx.world, [Spawner, Position, Health, Radius]);
    for (let i = 0; i < spawners.length; i++) {
      const s = spawners[i];
      if (this.visit[s] === stamp || Dying.flag[s] || Health.hp[s] <= 0) continue;
      const dx = Position.x[s] - x;
      const dy = Position.y[s] - y;
      const reach = range + Radius.r[s];
      const d2 = dx * dx + dy * dy;
      if (d2 >= reach * reach) continue;
      bestD = d2;
      best = s;
    }
    return best;
  }
}
